import React from "react";
import { Link } from "react-router-dom";
import { Heart, MessageCircle, Lock } from "lucide-react";
import { whatsappLink, formatINR } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { useAuth } from "@/context/AuthContext";
import client from "@/lib/api";
import { toast } from "sonner";

export default function ProductCard({ product, onWishlistChange }) {
    const { settings } = useSettings();
    const { user } = useAuth();
    const restricted = product.access_level === "vip" && !user;

    const priceDisplay = () => {
        if (product.price_visibility === "on_request" || product.price_visibility === "hidden") return "Price on Request";
        if (product.price == null) return "Price on Request";
        return formatINR(product.price);
    };

    const wa = whatsappLink(
        settings.whatsapp_number,
        `Hello Singh Jewellers,\n\nI am interested in:\nProduct: ${product.name}\nProduct Code: ${product.sku}\n\nPlease share availability, price and details.`
    );

    const addWish = async (e) => {
        e.preventDefault();
        if (!user) return toast.error("Please login to save favourites");
        try {
            await client.post(`/wishlist/${product.id}`);
            toast.success("Added to wishlist");
            onWishlistChange?.();
        } catch { toast.error("Could not add to wishlist"); }
    };

    const openWA = (e) => {
        e.preventDefault();
        e.stopPropagation();
        window.open(wa, "_blank", "noopener,noreferrer");
    };

    return (
        <Link to={`/products/${product.id}`} className="group block card-luxe" data-testid={`product-card-${product.id}`}>
            <div className="product-img-wrap aspect-[4/5]" onContextMenu={(e) => e.preventDefault()}>
                <img
                    src={product.images?.[0]}
                    alt={product.name}
                    loading="lazy"
                    draggable="false"
                    className={`w-full h-full object-cover transition-transform duration-700 group-hover:scale-105 ${restricted ? "blur-md" : ""}`}
                />
                <img className="watermark" src="/logo-watermark.png" alt="Singh Jewellers" />
                {product.new_arrival && (
                    <span className="absolute top-3 left-3 eyebrow text-white bg-[color:var(--charcoal)]/85 px-2.5 py-1 text-[9px]">New</span>
                )}
                {product.trending && (
                    <span className="absolute top-3 left-3 eyebrow text-white bg-[color:var(--champagne)]/95 px-2.5 py-1 text-[9px] mt-7">Trending</span>
                )}
                <button
                    onClick={addWish}
                    data-testid={`wishlist-btn-${product.id}`}
                    className="absolute top-3 right-3 bg-white/85 hover:bg-white rounded-full p-2 opacity-0 group-hover:opacity-100 transition-opacity"
                    aria-label="Add to wishlist"
                >
                    <Heart size={14} className="text-[color:var(--charcoal)]" />
                </button>
                {restricted && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center text-white bg-[color:var(--charcoal)]/50 z-10">
                        <Lock size={20} className="mb-2" />
                        <div className="eyebrow text-[color:var(--champagne-light)]">Exclusive</div>
                        <div className="text-sm mt-1">Login to view</div>
                    </div>
                )}
            </div>
            <div className="p-4">
                <div className="eyebrow text-[color:var(--muted-brown)] mb-1">{product.purity || product.metal}</div>
                <h3 className="font-serif-luxe text-lg text-[color:var(--charcoal)] leading-snug mb-1">{product.name}</h3>
                <div className="text-[11px] tracking-widest text-[color:var(--muted-brown)] mb-3">{product.sku}</div>
                <div className="flex items-end justify-between">
                    <div className="font-serif-luxe text-lg text-[color:var(--charcoal)]">{priceDisplay()}</div>
                    <button
                        type="button"
                        onClick={openWA}
                        className="text-[color:var(--whatsapp)] hover:opacity-80"
                        data-testid={`wa-enquire-${product.id}`}
                        aria-label="Enquire on WhatsApp"
                    >
                        <MessageCircle size={18} />
                    </button>
                </div>
            </div>
        </Link>
    );
}
